import { ArrowLeft, Plus } from "lucide-react";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button, PermissionGuard } from "@/components";
import { useI18n } from "@/context/i18n";

import { useClients } from "./clients.hooks";
import { TClientExtendedResponse } from "./clients.types";
import { ClientPreRegisterForm } from "./components/client-form/client-pre-register-form";
import { ClientProductsForm } from "./components/client-form/client-products-form";
import { ClientReviewData } from "./components/client-review-data";
import { TableClients } from "./components/table-clients";

export const ClientsPage: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const openClientIdFromNav =
    (location.state as { openClientId?: string } | null)?.openClientId ?? null;

  const {
    clients,
    client,
    selectedClient,
    clientForm,
    clientProductsForm,
    clientProductsFormSearch,
    openClientForm,
    selectClient,
    openClientReview,
    handleClientFormSubmit,
    handleAddressBlur,
    deleteClient,
    isClientFormOpen,
    setIsClientFormOpen,
    closeClientForm,
    isClientReviewOpen,
    setIsClientReviewOpen,
    isClientConfigProductsOpen,
    setIsClientConfigProductsOpen,
    filteredProducts,
    handleSearchProducts,
    clearProductsFilter,
    activeTab,
    setActiveTab,
    handleSubmitPlans,
    submitUpdatePlan,
    isEditingPlanFlow,
    setSelectedClient,
  } = useClients();

  // Ref para rastrear se o usuário fechou manualmente a visualização
  const manuallyClosedRef = React.useRef(false);

  // Prefill de planos em edição movido para useClients (regra de hooks da página)

  // Resetar o flag quando a visualização é aberta (de qualquer forma)
  React.useEffect(() => {
    if (isClientReviewOpen) {
      manuallyClosedRef.current = false;
    }
  }, [isClientReviewOpen]);

  // Ref para rastrear se já processamos o openClientId do state
  const processedOpenClientIdRef = React.useRef<string | null>(null);

  // Verificar se há um clientId no estado da navegação para abrir automaticamente
  React.useEffect(() => {
    const openClientId = openClientIdFromNav || undefined;
    
    // Só executar se houver state com openClientId E se não estiver já aberto
    // E se o openClientId for diferente do cliente atualmente selecionado
    // E se o usuário não fechou manualmente a visualização
    // E se ainda não processamos este openClientId
    if (
      openClientId &&
      !isClientReviewOpen &&
      openClientId !== selectedClient?.id &&
      !manuallyClosedRef.current &&
      processedOpenClientIdRef.current !== openClientId
    ) {
      // Marcar como processado antes de abrir
      processedOpenClientIdRef.current = openClientId;
      
      openClientReview(openClientId);
      
      // Limpar o estado para evitar reabrir ao navegar novamente
      // Usar setTimeout para garantir que o React Router está pronto e evitar loop
      const timeoutId = setTimeout(() => {
        try {
          navigate(location.pathname, { replace: true, state: {} });
        } catch (error) {
          console.error('Erro ao navegar:', error);
        }
      }, 0);
      
      return () => clearTimeout(timeoutId);
    }
    
    // Resetar o ref quando não há openClientId no state
    if (!openClientId && processedOpenClientIdRef.current) {
      processedOpenClientIdRef.current = null;
    }
  }, [
    openClientIdFromNav,
    openClientReview,
    isClientReviewOpen,
    navigate,
    location.pathname,
    selectedClient?.id,
  ]);

  return (
    <div className="flex flex-col w-full h-full space-y-6 p-4">
      <div className="flex flex-row items-start justify-between">
        <div className="flex flex-col w-full px-2">
          <div className="flex items-center gap-3 mb-3">
            {(isClientConfigProductsOpen || isClientReviewOpen) && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  if (isClientConfigProductsOpen) {
                    if (isEditingPlanFlow && selectedClient?.id) {
                      // Se está editando um plano, volta para a página "Ver planos" do parceiro
                      navigate(`/clients/${selectedClient.id}/plans`);
                    } else {
                      // Caso contrário, fecha o formulário de produtos e abre a visualização do parceiro
                      setIsClientConfigProductsOpen(false);
                      if (selectedClient?.id) {
                        openClientReview(selectedClient.id);
                      }
                    }
                  } else if (isClientReviewOpen) {
                    // Marcar que o usuário fechou manualmente a visualização
                    manuallyClosedRef.current = true;
                    // Volta para a lista de parceiros e limpa o state da navegação
                    setIsClientReviewOpen(false);
                    // Limpar o state para evitar que o useEffect reabra a visualização
                    navigate(location.pathname, { replace: true, state: {} });
                  }
                }}
                size="sm"
                className="p-3 hover:bg-muted/50 transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              {isClientReviewOpen && client ? client.name : t("clients.title")}
            </h1>
          </div>
          
          {isClientConfigProductsOpen && selectedClient ? (
            <div className="space-y-1">
              {isEditingPlanFlow && clientProductsForm.watch("planos")?.[0]?.name ? (
                <>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-muted-foreground">Parceiro:</span>
                    <span className="text-sm font-semibold text-foreground">{selectedClient.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-muted-foreground">Editando o plano:</span>
                    <span className="text-sm font-semibold text-primary">{clientProductsForm.watch("planos")[0].name}</span>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-muted-foreground">Parceiro:</span>
                  <span className="text-sm font-semibold text-foreground">{selectedClient.name}</span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t("clients.description")}
            </p>
          )}
        </div>
        {!isClientFormOpen && !isClientConfigProductsOpen && !isClientReviewOpen && (
          <div className="flex justify-end">
            <PermissionGuard permission="partners:create">
              <Button 
                onClick={openClientForm}
                className="shadow-sm hover:shadow-md transition-shadow"
              >
                <Plus className="mr-2 h-4 w-4" />
                {t("generic.new")}
              </Button>
            </PermissionGuard>
          </div>
        )}
      </div>
        {!isClientFormOpen &&
        !isClientReviewOpen &&
        !isClientConfigProductsOpen && (
          <div>
            {clients && clients.length > 0 ? (
              <TableClients
                clients={clients}
                openClientReview={openClientReview}
                deleteClient={deleteClient}
                openClientConfigProducts={setIsClientConfigProductsOpen}
                setSelectedClient={setSelectedClient}
              />
            ) : (
              <div className="flex flex-col items-center justify-center p-8">
                <p className="text-gray-500">Carregando clientes...</p>
              </div>
            )}
          </div>
        )}
      {isClientFormOpen && (
        <PermissionGuard
          permission={selectedClient ? "partners:edit" : "partners:create"}
        >
          <ClientPreRegisterForm
            selectedClient={selectedClient || null}
            form={clientForm}
            onSubmit={handleClientFormSubmit}
            setIsClientFormOpen={setIsClientFormOpen}
            onClose={closeClientForm}
            handleAddressBlur={handleAddressBlur}
          />
        </PermissionGuard>
      )}
      {isClientReviewOpen && client && (
        <ClientReviewData
          client={client as TClientExtendedResponse}
          setIsClientReviewOpen={setIsClientReviewOpen}
          openClientConfigProducts={setIsClientConfigProductsOpen}
          setSelectedClient={setSelectedClient}
          deleteClient={deleteClient}
          selectClient={selectClient}
        />
      )}
      {isClientConfigProductsOpen && (
        <ClientProductsForm
          form={clientProductsForm}
          searchForm={clientProductsFormSearch}
          onSubmit={(data) => {
            handleSubmitPlans(data);
          }}
          open={isClientConfigProductsOpen}
          onOpenChange={(open) => {
            setIsClientConfigProductsOpen(open);
            if (!open && selectedClient?.id) {
              // Quando fechar, voltar para a página de detalhes do parceiro
              openClientReview(selectedClient.id);
            }
          }}
          filteredProducts={filteredProducts}
          handleSearchProducts={handleSearchProducts}
          clearProductsFilter={clearProductsFilter}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          isEditing={isEditingPlanFlow}
          submitUpdatePlan={(index) => submitUpdatePlan(index)}
          clientId={selectedClient?.id}
        />
      )}
    </div>
  );
};
